import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ApiError, asList } from '../lib/api';

/**
 * ═══ THE OFFICE'S REGISTER OF WHAT FAMILIES HAVE SAID ═══
 *
 * Who has agreed, who has said no, and who has not been asked — a class at a time, because that is
 * how forms go home and come back.
 *
 * ⚠ THIS SCREEN EXISTS BEFORE THE MATCHER, AND THAT IS THE POINT. Consent takes a term to collect
 * and is the only part of face attendance that writing better code cannot shorten. A school that
 * starts the term this ships is ready when the model lands; one that waits starts a term behind.
 *
 * ⚠ "NOT ASKED" AND "DECLINED" ARE DIFFERENT COLUMNS OF THE SAME LIST. Merging them would have the
 * office chasing families who already said no, which is how a consent drive turns into pressure.
 */

interface Group { id: string; label: string; people: number }
interface Row { studentId: string; admissionNumber: string; fullName: string }
interface Roster { students: Row[] }

interface Consent {
  studentId: string;
  state: 'NotAsked' | 'Granted' | 'Declined' | 'Withdrawn';
  channel: string;
  guardianName: string | null;
  answeredAt: string;
  withdrawnAt: string | null;
  expiresOn: string | null;
  effective: boolean;
  awaitingTemplateDeletion: boolean;
  enrolled: boolean;
}

interface Readiness {
  studentsInScope: number; granted: number; declined: number; enrolled: number;
  withdrawn: number; notAsked: number; awaitingTemplateDeletion: number;
  notes: string[];
}

const STATE_TEXT: Record<Consent['state'], string> = {
  NotAsked: 'Not asked',
  Granted: 'Agreed',
  Declined: 'Said no',
  Withdrawn: 'Withdrawn',
};

const today = () => new Date().toISOString().slice(0, 10);

export function FaceConsentPage() {
  const [institutionId, setInstitutionId] = useState('');
  const [classes, setClasses] = useState<Group[]>([]);
  const [classLabel, setClassLabel] = useState('');
  const [rows, setRows] = useState<Row[]>([]);
  const [consents, setConsents] = useState<Record<string, Consent>>({});
  const [readiness, setReadiness] = useState<Readiness | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<{ studentId: string; name: string } | null>(null);

  useEffect(() => {
    api.get<Array<{ id: string }>>('/api/institutions')
      .then((xs) => setInstitutionId((i) => i || xs[0]?.id || '')).catch(() => undefined);
    api.get<Group[]>('/api/exports/register/groups?scope=students')
      .then((r) => setClasses(asList(r))).catch(() => setClasses([]));
  }, []);

  const load = useCallback(async () => {
    if (!institutionId || !classLabel) { setRows([]); setConsents({}); setReadiness(null); return; }
    setBusy(true); setError(null);
    try {
      const q = new URLSearchParams({ institutionId, date: today(), classLabel });
      const roster = await api.get<Roster>(`/api/attendance/roster?${q}`);
      setRows(roster.students);

      const studentIds = roster.students.map((s) => s.studentId);
      const [answers, ready] = await Promise.all([
        api.post<Consent[]>('/api/face-consent/lookup', { studentIds }),
        // ⚠ faceSwitchedOn is reported honestly rather than assumed. A school that has collected
        // every form and not switched the mode on should be told that, not congratulated.
        api.post<Readiness>('/api/face-consent/readiness', { studentIds, faceSwitchedOn: false }),
      ]);
      setConsents(Object.fromEntries(answers.map((a) => [a.studentId, a])));
      setReadiness(ready);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load the class.');
      setRows([]);
    } finally { setBusy(false); }
  }, [institutionId, classLabel]);

  useEffect(() => { void load(); }, [load]);

  const record = async (studentId: string, granted: boolean, guardianName: string) => {
    setBusy(true); setError(null);
    try {
      await api.post('/api/face-consent/paper-form', { studentId, granted, guardianName });
      setForm(null);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not record the form.');
    } finally { setBusy(false); }
  };

  const withdraw = async (studentId: string) => {
    setBusy(true); setError(null);
    try {
      await api.post(`/api/face-consent/${studentId}/withdraw`, {});
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not record the withdrawal.');
    } finally { setBusy(false); }
  };

  const counts = useMemo(() => {
    const c = { NotAsked: 0, Granted: 0, Declined: 0, Withdrawn: 0 };
    for (const r of rows) c[consents[r.studentId]?.state ?? 'NotAsked']++;
    return c;
  }, [rows, consents]);

  return (
    <div>
      <div style={{ marginBottom: 18 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>Attendance</div>
        <h1 className="h-display" style={{ fontSize: 28 }}>Face recognition consent</h1>
        <p style={{ color: 'var(--ink-2)', fontSize: 13, marginTop: 4 }}>
          What each family has said. Parents can answer in the app; paper forms are keyed here.
        </p>
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
        <label className="label" style={{ margin: 0 }}>Class</label>
        <select className="input" value={classLabel} onChange={(e) => setClassLabel(e.target.value)}
                style={{ width: 'auto', minWidth: 200 }}>
          <option value="">Choose a class</option>
          {classes.map((c) => <option key={c.id} value={c.label}>{c.label} · {c.people}</option>)}
        </select>
      </div>

      {error && (
        <div className="card" style={{ padding: 14, marginBottom: 14, borderLeft: '3px solid var(--danger, #c0392b)' }}>
          {error}
        </div>
      )}

      {readiness && rows.length > 0 && (
        <div className="card" style={{ padding: 18, marginBottom: 16, maxWidth: 720 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Where this class has got to</div>
          <div style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 10 }}>
            {counts.Granted} agreed · {counts.Declined} said no · {counts.Withdrawn} withdrawn ·{' '}
            {counts.NotAsked} not asked, of {rows.length}
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: 'var(--ink-2)' }}>
            {readiness.notes.map((n) => <li key={n} style={{ marginBottom: 4 }}>{n}</li>)}
          </ul>
        </div>
      )}

      {rows.length === 0
        ? <div className="card" style={{ padding: 24, textAlign: 'center', color: 'var(--ink-3)' }}>
            {busy ? 'Loading…' : 'Choose a class to see what its families have said.'}
          </div>
        : <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {rows.map((r) => {
              const c = consents[r.studentId];
              const state = c?.state ?? 'NotAsked';
              return (
                <div key={r.studentId} className="card"
                     style={{ padding: 14, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                    <div style={{ fontWeight: 600 }}>{r.fullName}</div>
                    <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                      {r.admissionNumber}
                      {c?.guardianName ? ` · ${c.guardianName}` : ''}
                      {c && state !== 'NotAsked'
                        ? ` · ${c.channel === 'PaperForm' ? 'paper form' : 'in the app'}`
                        : ''}
                    </div>
                  </div>

                  <div style={{ fontSize: 13, minWidth: 110 }}>
                    {STATE_TEXT[state]}
                    {c?.enrolled && <span style={{ color: 'var(--ink-3)' }}> · enrolled</span>}
                    {c?.awaitingTemplateDeletion &&
                      // ⚠ Shown, because a withdrawal whose deletion never completed is the one
                      // thing on this screen a school would actually be in trouble for.
                      <span style={{ color: 'var(--warn)' }}> · deletion pending</span>}
                  </div>

                  <div style={{ display: 'flex', gap: 8 }}>
                    {state === 'Granted' && c?.effective
                      ? <button className="btn" disabled={busy} onClick={() => withdraw(r.studentId)}>Withdraw</button>
                      : <button className="btn" disabled={busy}
                                onClick={() => setForm({ studentId: r.studentId, name: '' })}>
                          Record a form
                        </button>}
                  </div>

                  {/* ⚠ INSIDE THE ROW, NOT AT THE BOTTOM OF THE PAGE. The panel used to render
                      after the list: in a class of forty-five, clicking "Record a form" on the
                      fortieth child opened a form far below, off screen, with nobody's name on it —
                      and the office would key the wrong child's answer without ever seeing that
                      they had. It is here, under the child it is about, and it says so. */}
                  {form?.studentId === r.studentId && (
                    <div style={{ flexBasis: '100%', borderTop: '1px solid var(--line, #e5e5e5)',
                                  marginTop: 12, paddingTop: 12 }}>
                      <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 14 }}>
                        Record a signed paper form for {r.fullName}
                      </div>
                      <p style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 0, marginBottom: 10 }}>
                        Keep the form. This records who signed it and that you keyed it.
                      </p>
                      <input className="input" placeholder="Name of the parent or guardian who signed"
                             value={form.name} autoFocus
                             onChange={(e) => setForm({ ...form, name: e.target.value })}
                             style={{ marginBottom: 10, maxWidth: 380 }} />
                      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                        {/* ⚠ Both buttons plain, both disabled until a name is typed. A form with
                            nobody's name on it is not a record of anything, and the domain refuses
                            it anyway — this is so the office finds that out before pressing. */}
                        <button className="btn" disabled={busy || !form.name.trim()}
                                onClick={() => record(r.studentId, true, form.name.trim())}>
                          They agreed
                        </button>
                        <button className="btn" disabled={busy || !form.name.trim()}
                                onClick={() => record(r.studentId, false, form.name.trim())}>
                          They said no
                        </button>
                        <button className="btn" onClick={() => setForm(null)}>Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>}

    </div>
  );
}
